// size-info.ts — client-side render of the creature SIZE breakdown panel, reading from
// window.FateTide.casterCache (no DB ping on a hit). Replaces the PHP fetch
// options/tools/encounter_creatures/encounter_creature_info/size_info_inc.php.
//
// Data: computed.creature.size.breakdown { base{size_id,source_id?,source_label,source_kind},
// events[], final_size_id } already rides in the blob (recalc_creature_size). The ONE piece the
// cache historically lacked — the final size term's item_name + item_description — is now interned
// into the caster ROOT .catalog by _ft_intern_size_identity (functions.php), so the header + the
// description box render with zero network. (cf. movement-sense-info, which DROPS its term text.)
//
// Faithful port of render_size_transitions_breakdown_block(): base row, per-event transition rows
// (set size / ±N ladder step, from→to size icons), and the "Final size" footer. Source links drill
// in via ftDrillFromBreakdown (cache-first openItemInfo + Back-to-breakdown), like the other panels.

import { setActiveInfoPanel, paintActiveInfoPanel } from './active-panel';
import { parseText } from '../shared/parse-text';
import type { CachedCaster } from './types';

interface SizeBase { size_id?: string; source_id?: string; source_label?: string; source_kind?: string }
interface SizeEvent {
    kind?: string; source_id?: string; source_kind?: string; source_label?: string;
    from_size_id?: string; to_size_id?: string; delta?: number;
}
interface SizeBreakdown { base?: SizeBase; events?: SizeEvent[]; final_size_id?: string }

const isUuid = (s: unknown): boolean =>
    typeof s === 'string' && /^[0-9a-fA-F-]{36}$/.test(s);

// source_kind → icon-registry slug. The breakdown stores the base row's kind as 'ancestral'
// (the data adjective), but the icon is keyed 'ancestry' — without this remap iconMap('ancestral')
// renders the missing-icon "?". Other kinds (passive_ability, feat, …) already match their slug.
const ICON_KIND_ALIAS: Record<string, string> = { ancestral: 'ancestry' };
const iconKindSlug = (kind: string): string => ICON_KIND_ALIAS[kind] ?? kind;

function sizeBreakdown(caster: CachedCaster): SizeBreakdown | null {
    const bd = caster?.computed?.creature?.size?.breakdown;
    return bd && typeof bd === 'object' ? (bd as SizeBreakdown) : null;
}

/** Resolve a UUID → {name, description} from the caster's OWN root .catalog (interned server-side). */
function catalogTerm(caster: CachedCaster, id: string): { name: string; description: string } {
    const cat = caster.catalog;
    const e = cat && (cat[id] ?? cat[String(id).toUpperCase()] ?? cat[String(id).toLowerCase()]);
    if (!e || typeof e !== 'object') return { name: '', description: '' };
    const src = e.items && typeof e.items === 'object' && !Array.isArray(e.items)
        ? e.items as Record<string, unknown>
        : e;
    const pick = (k: string): string => {
        const v = Array.isArray(src[k]) ? src[k][0] : src[k];
        return typeof v === 'string' ? v : '';
    };
    return { name: pick('item_name'), description: pick('item_description') };
}

/** UUID → display name, catalog-first then the cross-root resolver. */
function nameById(caster: CachedCaster, id: string): string {
    const t = catalogTerm(caster, id).name;
    if (t !== '') return t;
    return typeof ftItemNameById === 'function' ? String(ftItemNameById(id) ?? '') : '';
}

/** A clickable source label that drills into the contributing item (cache-first), Back-aware. */
function sourceLabelHtml(id: string, label: string): string {
    const text = escapeHtml(label);
    if (!isUuid(id)) return text;
    return `<span role="button" class="link-primary" onclick="ftDrillFromBreakdown('${escapeHtml(id)}')">${text}</span>`;
}

function rowOpen(): string {
    return `<div class="list-group-item d-flex justify-content-between align-items-center gap-2 flex-wrap py-2 w-100">`;
}

function renderTransitions(caster: CachedCaster, bd: SizeBreakdown): string {
    let html = `<div class="list-group list-group-flush border rounded overflow-hidden small text-start w-100 d-flex flex-column align-items-stretch">`;

    // Base row.
    const base = bd.base ?? {};
    if (isUuid(base.size_id)) {
        const kind = iconKindSlug(base.source_kind || 'ancestry');
        const label = base.source_label || 'Base';
        html += rowOpen()
            + `<span class="d-flex align-items-center gap-2 text-start flex-grow-1 min-w-0">`
            + `<span class="flex-shrink-0 d-inline-flex align-items-center" aria-hidden="true">${iconMap(kind, 22, 22)}</span>`
            + `<span class="min-w-0">${sourceLabelHtml(String(base.source_id ?? ''), label)}</span>`
            + `</span>`
            + `<span class="d-inline-flex align-items-center gap-1 flex-shrink-0"><span class="d-inline-flex" aria-hidden="true">${iconMap(String(base.size_id), 20, 20)}</span></span>`
            + `</div>`;
    }

    // Event (transition) rows.
    for (const ev of bd.events ?? []) {
        const h = String(ev.source_id ?? '');
        const iconKind = iconKindSlug(ev.source_kind || 'passive_ability');
        let label = (ev.source_label ?? '').trim();
        if (label === '' && isUuid(h)) label = nameById(caster, h);
        if (label === '') label = 'Unknown source';

        let change = '';
        if (ev.kind === 'direct_set') {
            change = `<span class="text-muted ms-1">set size</span>`;
        } else if (ev.kind === 'step_change' && typeof ev.delta === 'number') {
            const d = ev.delta;
            change = `<span class="text-muted ms-1">${d >= 0 ? '+' : ''}${d}</span><span class="text-muted"> ladder step</span>`;
        }

        const labelHtml = isUuid(h)
            ? `${sourceLabelHtml(h, label)}:`
            : `<strong>${escapeHtml(label)}</strong>:`;

        const f = String(ev.from_size_id ?? '');
        const t = String(ev.to_size_id ?? '');
        html += rowOpen()
            + `<span class="d-flex align-items-center gap-2 text-start flex-grow-1 min-w-0">`
            + `<span class="flex-shrink-0 d-inline-flex align-items-center" aria-hidden="true">${iconMap(iconKind, 22, 22)}</span>`
            + `<span class="min-w-0">${labelHtml}${change}</span>`
            + `</span>`
            + `<span class="d-inline-flex align-items-center gap-1 flex-shrink-0">`
            + (isUuid(f) ? `<span class="d-inline-flex" aria-hidden="true">${iconMap(f, 20, 20)}</span>` : '')
            + `<span class="text-muted small">→</span>`
            + (isUuid(t) ? `<span class="d-inline-flex" aria-hidden="true">${iconMap(t, 20, 20)}</span>` : '')
            + `</span>`
            + `</div>`;
    }

    // Final size footer.
    const fin = String(bd.final_size_id ?? '').trim();
    if (isUuid(fin)) {
        html += `<div class="list-group-item list-group-item-light d-flex align-items-center justify-content-between gap-2 py-2 w-100 fw-semibold">`
            + `<div class="d-flex align-items-center gap-2 flex-grow-1 min-w-0">`
            + `<span class="flex-shrink-0 d-inline-flex align-items-center" aria-hidden="true">${iconMap('size', 22, 22)}</span>`
            + `<span>Final size</span></div>`
            + `<div class="d-inline-flex align-items-center justify-content-end flex-shrink-0 gap-2">`
            + `<span class="d-inline-flex lh-1" aria-hidden="true">${iconMap(fin, 26, 26)}</span>`
            + `<span class="badge bg-secondary">${escapeHtml(nameById(caster, fin))}</span></div></div>`;
    }

    html += `</div>`;
    return html;
}

function renderSizePanel(caster: CachedCaster): string {
    const bd = sizeBreakdown(caster);
    const finalId = String(bd?.final_size_id ?? '').trim();
    if (!isUuid(finalId)) {
        return '<div class="p-3 text-center text-danger">Unable to resolve creature size.</div>';
    }
    const term = catalogTerm(caster, finalId);
    const name = term.name || nameById(caster, finalId) || 'Size';

    let html = `<table class="table border">
        <thead><tr><th scope="col" colspan="2" class="table-dark align-middle">${iconMap('size')} ${escapeHtml(name)}</th></tr></thead>
        <tbody>
            <tr><th scope="row" class="align-middle">Compiled size</th>
                <td class="text-end"><h3 class="mb-0"><span class="d-inline-flex justify-content-end align-items-center gap-2 lh-1">${iconMap(finalId, 28, 28)}<span class="badge bg-primary">${escapeHtml(name)}</span></span></h3></td></tr>
        </tbody>
    </table>`;

    if (term.description !== '') {
        html += `<div class="text-start border rounded p-3 mb-3 small">${parseText(term.description)}</div>`;
    }

    html += `<div class="mb-2"><div class="small text-muted mb-1">Transitions &amp; sources</div>${renderTransitions(caster, bd!)}</div>`;
    return html;
}

/**
 * Entry point. Repointed onclick for the size chips (character-description.ts tab + the
 * creature card header). casterId keys window.FateTide.casterCache; cache-first with a
 * fetchAndCacheCaster fallback, mirroring renderMovementSenseInfoPanel.
 */
export async function renderSizeInfoPanel(
    casterId: string,
    targetId = 'div_refresh_side_info',
): Promise<void> {
    const target = document.getElementById(targetId);
    if (!target) return;

    let caster = window.FateTide?.casterCache?.get(casterId) as CachedCaster | undefined;
    if (!caster) {
        target.innerHTML = '<div class="p-3 text-center text-muted">Loading…</div>';
        try {
            await fetchAndCacheCaster(casterId);
        } catch {
            // fetchAndCacheCaster swallows its own errors; fall through to the miss guard.
        }
        caster = window.FateTide?.casterCache?.get(casterId) as CachedCaster | undefined;
    }
    if (!caster) {
        target.innerHTML = '<div class="p-3 text-center text-danger">Could not load creature data.</div>';
        return;
    }

    const render = (c: CachedCaster) => renderSizePanel(c);
    setActiveInfoPanel(casterId, targetId, render);
    paintActiveInfoPanel(caster);
}
